import React, { useCallback, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { findGuide, guideImage } from '../../content/shopGuides';
import { findProduct } from '../../content/products';
import { installTiers } from '../../content/products';
import { incFromEx, toCents, fmt } from '../../lib/shopPricing';
import { ProductCard, Breadcrumbs, Html, Section } from '../../components/shop/ShopBits';
import LockWizard from '../../components/shop/LockWizard';
import PhotoLightbox from '../../components/shop/PhotoLightbox';
import usePageMeta from '../../hooks/usePageMeta';
import NotFound from '../NotFound';

// /shop/guides/:slug — a photographed, numbered walk-through of a real install.
const GuidePage = () => {
  const { slug } = useParams();
  const guide = findGuide(slug);
  const [open, setOpen] = useState(null);
  const count = guide ? guide.steps.length : 0;
  const close = useCallback(() => setOpen(null), []);
  const move = useCallback((d) => setOpen((i) => (i === null ? null : Math.min(count - 1, Math.max(0, i + d)))), [count]);
  usePageMeta();
  if (!guide) return <NotFound />;
  const related = (guide.related || []).map(findProduct).filter(Boolean);
  const cut = incFromEx(toCents(installTiers.cut.exGst));

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gray-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-red-400 mb-3">Install guide</p>
            <h1 className="text-3xl sm:text-5xl font-bold mb-4">{guide.title}</h1>
            <p className="text-lg text-gray-200">{guide.summary}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/shop/installation">
                <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold px-6">
                  Book this install from {fmt(cut)} <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="#steps">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-6">
                  See the {guide.steps.length} steps
                </Button>
              </a>
            </div>
          </div>
          <button type="button" onClick={() => setOpen(guide.hero - 1)} className="block w-full text-left" aria-label={`Enlarge: ${guide.steps[guide.hero - 1].title}`}>
            <img src={guideImage(guide, guide.hero)} srcSet={`${guideImage(guide, guide.hero, true)} 450w, ${guideImage(guide, guide.hero)} 900w`} sizes="(min-width: 1024px) 560px, 100vw" alt={guide.steps[guide.hero - 1].title} className="w-full max-h-[420px] object-cover rounded-2xl border border-gray-800" />
          </button>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'Guides', path: '/shop/installation' }, { name: guide.title }]} />
        <Html html={guide.intro} className="max-w-3xl mb-4" />
        <p className="text-sm text-gray-600 mb-8">Tap any photo to see it full size; the arrows step through the job.</p>

        <ol id="steps" className="scroll-mt-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guide-steps">
          {guide.steps.map((s, i) => (
            <li key={s.title} className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
              <button type="button" onClick={() => setOpen(i)} className="group relative block w-full bg-gray-100 text-left" aria-label={`Enlarge step ${i + 1}: ${s.title}`}>
                <img src={guideImage(guide, i + 1, true)} srcSet={`${guideImage(guide, i + 1, true)} 450w, ${guideImage(guide, i + 1)} 900w`} sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw" alt={`Step ${i + 1}: ${s.title}`} loading={i < 3 ? 'eager' : 'lazy'} width={450} height={600} className="w-full aspect-[3/4] object-contain" />
                <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-xs px-2 py-1 group-hover:bg-black/80"><Maximize2 size={12} /> Enlarge</span>
              </button>
              <div className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Step {i + 1} of {guide.steps.length}</p>
                <h2 className="font-bold text-gray-900 mt-1">{s.title}</h2>
                <p className="text-sm text-gray-700 mt-1">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>

        <Html html={guide.outro} className="max-w-3xl mt-10" />
      </div>

      {open !== null && <PhotoLightbox items={guide.steps.map((st, i) => ({ src: guideImage(guide, i + 1), title: st.title, text: st.text }))} index={open} onClose={close} onMove={move} label="Step" testId="guide-lightbox" />}

      <Section title="Which lock is right for your door?" className="bg-gray-50">
        <LockWizard testId="guide-chooser" compact />
      </Section>

      {related.length > 0 && (
        <Section title="Locks we fit this way">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export default GuidePage;
